<div class="rename-content">
  <p-confirmDialog></p-confirmDialog>

  <div class="header">
    <h2>Content Rename</h2>
    <p class="subtitle">
      Dry-run first (read-only preview). Optionally narrow to Mongo or MySQL and specific
      collections/tables — leave Database empty for the full scan. All matches are selected by
      default — including specials. Review specials carefully and uncheck any you want to discard, then Apply.
    </p>
  </div>

  <div *ngIf="!isAdmin" class="admin-only">
    <i class="pi pi-lock"></i>
    <span>This tool requires <strong>ROLE_ADMIN</strong>.</span>
  </div>

  <div class="panel" *ngIf="isAdmin">
    <div class="form-row">
      <div class="field">
        <label for="from">From</label>
        <input
          id="from"
          pInputText
          [(ngModel)]="from"
          [disabled]="loading"
          placeholder="e.g. Garden Joy"
        />
      </div>
      <div class="field">
        <label for="to">To</label>
        <input
          id="to"
          pInputText
          [(ngModel)]="to"
          [disabled]="loading"
          placeholder="e.g. Flourish"
        />
      </div>
    </div>

    <div class="form-row">
      <div class="field">
        <label for="dbSource">Database</label>
        <p-dropdown
          inputId="dbSource"
          [options]="dbSourceOptions"
          [(ngModel)]="dbSource"
          (ngModelChange)="onDbSourceChange($event)"
          optionLabel="label"
          optionValue="value"
          placeholder="All (default)"
          [showClear]="true"
          [disabled]="loading"
          styleClass="w-full"
        ></p-dropdown>
        <small class="field-hint">Leave empty to scan Mongo + MySQL (same as before).</small>
      </div>
      <div class="field" *ngIf="dbSource">
        <label for="targets">{{ targetsLabel }}</label>
        <p-multiSelect
          inputId="targets"
          [options]="targetOptions"
          [(ngModel)]="selectedTargets"
          optionLabel="label"
          optionValue="value"
          [filter]="true"
          filterBy="label"
          [disabled]="loading || loadingTargets"
          placeholder="All {{ targetsLabel | lowercase }} (default)"
          [showClear]="true"
          display="chip"
          appendTo="body"
          styleClass="w-full"
          [maxSelectedLabels]="3"
          selectedItemsLabel="{0} selected"
        ></p-multiSelect>
        <small class="field-hint" *ngIf="loadingTargets">Loading {{ targetsLabel | lowercase }}…</small>
        <small class="field-hint" *ngIf="!loadingTargets">
          Optional — leave empty to include all {{ targetsLabel | lowercase }} in {{ dbSource }}.
        </small>
      </div>
    </div>

    <div class="actions">
      <button
        pButton
        type="button"
        label="Dry-run"
        icon="pi pi-eye"
        class="p-button-outlined"
        [disabled]="loading"
        (click)="dryRun()"
      ></button>
      <button
        pButton
        type="button"
        label="Apply selected"
        icon="pi pi-check"
        class="p-button-danger"
        [disabled]="!canApply"
        (click)="confirmApply()"
        pTooltip="Applies all checked rows (safe and special). Uncheck rows to discard them."
        tooltipPosition="top"
      ></button>
      <button
        pButton
        type="button"
        label="Rollback"
        icon="pi pi-replay"
        class="p-button-warning"
        [disabled]="!canRollback"
        (click)="confirmRollback()"
        pTooltip="Review and restore values from the last successful Apply (kept up to 1 month)."
        tooltipPosition="top"
      ></button>
      <button
        pButton
        type="button"
        label="Discovery scan"
        icon="pi pi-search"
        class="p-button-secondary"
        [disabled]="loading"
        (click)="discoveryScan()"
        pTooltip="Full-database read-only scan using the From value"
        tooltipPosition="top"
      ></button>
      <button
        pButton
        type="button"
        label="Download JSON"
        icon="pi pi-download"
        class="p-button-text"
        [disabled]="loading || !hasResults"
        pTooltip="Download apply/dry-run/scan report as JSON"
        tooltipPosition="top"
      ></button>
      <button
        pButton
        type="button"
        label="Download PDF"
        icon="pi pi-file-pdf"
        class="p-button-text"
        [disabled]="loading || !hasResults || !resultMode"
        (click)="downloadPdf()"
        pTooltip="Apply report after Apply; dry-run/scan otherwise. Opens print dialog — Save as PDF."
        tooltipPosition="top"
      ></button>
    </div>

    <div class="hint" *ngIf="lastDryRun?.success && !canApply && resultMode === 'dry-run' && !loading">
      <span *ngIf="selectableChanges.length === 0">
        Dry-run found no changes to apply.
      </span>
      <span *ngIf="selectableChanges.length > 0 && selectedCount === 0">
        All rows are unchecked — select at least one to apply.
      </span>
      <span *ngIf="(lastDryRun.specialChangeCount || 0) > 0">
        {{ lastDryRun.specialChangeCount }} special case(s) are selected — uncheck any you want to discard.
      </span>
    </div>
    <div class="hint success" *ngIf="canApply">
      Ready: {{ selectedCount }} selected
      ({{ selectedSafeCount }} safe<span *ngIf="selectedSpecialCount > 0">, {{ selectedSpecialCount }} special</span>)
      <span *ngIf="discardedKeys.length > 0"> · {{ discardedKeys.length }} discarded</span>
    </div>
    <div class="hint success" *ngIf="canRollback && resultMode !== 'rollback'">
      <ng-container *ngIf="resultMode === 'apply'; else pendingRollbackHint">
        Apply succeeded — {{ lastApply?.appliedCount || 0 }} change(s) written
        <span *ngIf="(lastApply?.discardedCount || 0) > 0">
          · {{ lastApply?.discardedCount }} discarded
        </span>
        · results below are the applied rows · Download PDF/JSON for the apply report
        · Rollback available up to 1 month
      </ng-container>
      <ng-template #pendingRollbackHint>
        Last Apply available for rollback:
        "{{ pendingRollback?.from }}" → "{{ pendingRollback?.to }}"
        · {{ pendingRollback?.appliedCount || rollbackChanges.length }} change(s)
        <span *ngIf="pendingRollback?.appliedAt">
          · applied {{ pendingRollback?.appliedAt | date: 'medium' }}
        </span>
        · kept up to 1 month
      </ng-template>
    </div>
    <div class="hint" *ngIf="resultMode === 'rollback' && lastApply?.success && !loading">
      Rollback complete. Previous values have been restored.
    </div>
    <div class="hint" *ngIf="lastDryRun?.note || lastScan?.note || lastApply?.note">
      {{ lastDryRun?.note || lastScan?.note || lastApply?.note }}
    </div>
  </div>

  <div class="results" *ngIf="resultMode || loading">
    <div class="results-loading" *ngIf="loading">
      <i class="pi pi-spin pi-spinner"></i>
      <div class="results-loading-text">
        <strong>Processing</strong>
        <span>{{ loadingLabel }}</span>
      </div>
    </div>

    <ng-container *ngIf="resultMode && !loading">
    <div class="results-header">
      <h3>
        {{
          resultMode === 'apply'
            ? 'Apply results'
            : resultMode === 'rollback'
              ? 'Rollback results'
              : resultMode === 'scan'
                ? 'Discovery results'
                : 'Results'
        }}
      </h3>
      <span class="summary">{{ summaryText }}</span>
    </div>

    <div class="selection-toolbar" *ngIf="resultMode === 'dry-run'">
      <div class="select-control">
        <p-checkbox
          inputId="renameSelectAllResults"
          [binary]="true"
          [ngModel]="allSelectedChecked"
          [disabled]="selectableChanges.length === 0 || loading"
          (onChange)="onAllResultsChange($event)"
        ></p-checkbox>
        <label for="renameSelectAllResults">All results ({{ selectableChanges.length }})</label>
      </div>
      <span class="selection-meta">{{ selectedCount }} selected · {{ discardedKeys.length }} discarded</span>
    </div>

    <div class="results-body">
    <p-table
      *ngIf="resultMode !== 'scan'"
      [value]="runChanges"
      [paginator]="true"
      [rows]="resultsRows"
      [first]="resultsFirst"
      [rowsPerPageOptions]="[25, 50, 100]"
      (onPage)="onResultsPage($event)"
      styleClass="p-datatable-sm p-datatable-striped rename-results-table"
      [scrollable]="true"
      scrollHeight="480px"
      [tableStyle]="{ 'min-width': '72rem' }"
    >
      <ng-template pTemplate="header">
        <tr>
          <th class="col-check" *ngIf="resultMode === 'dry-run'">
            <p-checkbox
              inputId="renameSelectPage"
              [binary]="true"
              [ngModel]="pageSelectedChecked"
              [indeterminate]="pagePartiallySelected"
              [disabled]="currentPageSelectable.length === 0 || loading"
              (onChange)="onPageResultsChange($event)"
              pTooltip="Select / unselect this page only"
              tooltipPosition="top"
            ></p-checkbox>
          </th>
          <th class="col-flags">Flags</th>
          <th class="col-source">Source</th>
          <th class="col-collection">Collection / Table</th>
          <th class="col-id">ID</th>
          <th class="col-field">Field / Column</th>
          <th class="col-value">Old value</th>
          <th class="col-value">
            {{
              resultMode === 'dry-run'
                ? 'Proposed value'
                : resultMode === 'rollback'
                  ? 'Restored value'
                  : 'New value'
            }}
          </th>
        </tr>
      </ng-template>
      <ng-template pTemplate="body" let-row>
        <tr
          [class.special-row]="row.isSpecialCase"
          [class.discarded-row]="resultMode === 'dry-run' && !isRowSelected(row)"
        >
          <td class="col-check" *ngIf="resultMode === 'dry-run'">
            <p-checkbox
              [binary]="true"
              [ngModel]="isRowSelected(row)"
              [disabled]="loading || !row.key"
              (onChange)="toggleRow(row, $event.checked)"
              [pTooltip]="row.isSpecialCase ? 'Special case — review carefully; uncheck to discard' : ''"
              tooltipPosition="top"
            ></p-checkbox>
          </td>
          <td class="col-flags">
            <span *ngIf="row.isSpecialCase" class="flag special" [pTooltip]="formatReasons(row.specialReasons)">
              Special
            </span>
            <span *ngIf="!row.isSpecialCase" class="flag safe">Safe</span>
          </td>
          <td class="col-source">{{ row.source }}</td>
          <td class="col-collection">
            <div class="clip-cell" [pTooltip]="row.collectionOrTable" tooltipPosition="top">
              {{ row.collectionOrTable }}
            </div>
          </td>
          <td class="col-id">
            <div class="id-cell" [pTooltip]="row.id" tooltipPosition="top">{{ row.id }}</div>
          </td>
          <td class="col-field">
            <div class="clip-cell" [pTooltip]="row.fieldOrColumn" tooltipPosition="top">
              {{ row.fieldOrColumn }}
            </div>
            <div class="reasons" *ngIf="row.isSpecialCase">{{ formatReasons(row.specialReasons) }}</div>
          </td>
          <td class="col-value">
            <div class="value-cell">{{ row.oldValue }}</div>
          </td>
          <td class="col-value">
            <div class="value-cell new">{{ row.newValue }}</div>
          </td>
        </tr>
      </ng-template>
      <ng-template pTemplate="emptymessage">
        <tr>
          <td [attr.colspan]="resultMode === 'dry-run' ? 8 : 7">No changes.</td>
        </tr>
      </ng-template>
    </p-table>

    <p-table
      *ngIf="resultMode === 'scan'"
      [value]="scanRows"
      [paginator]="true"
      [rows]="25"
      [rowsPerPageOptions]="[25, 50, 100]"
      styleClass="p-datatable-sm p-datatable-striped rename-results-table"
      [scrollable]="true"
      scrollHeight="480px"
      [tableStyle]="{ 'min-width': '64rem' }"
    >
      <ng-template pTemplate="header">
        <tr>
          <th class="col-flags">Flags</th>
          <th class="col-source">Source</th>
          <th class="col-collection">Collection / Table</th>
          <th class="col-id">ID</th>
          <th class="col-field">Field / Column</th>
          <th class="col-value">Value</th>
        </tr>
      </ng-template>
      <ng-template pTemplate="body" let-row>
        <tr [class.special-row]="row.isSpecialCase">
          <td class="col-flags">
            <span *ngIf="row.isSpecialCase" class="flag special" [pTooltip]="formatReasons(row.specialReasons)">
              Special
            </span>
            <span *ngIf="!row.isSpecialCase" class="flag safe">Safe</span>
          </td>
          <td class="col-source">{{ row.source }}</td>
          <td class="col-collection">
            <div class="clip-cell" [pTooltip]="row.collectionOrTable" tooltipPosition="top">
              {{ row.collectionOrTable }}
            </div>
          </td>
          <td class="col-id">
            <div class="id-cell" [pTooltip]="row.id" tooltipPosition="top">{{ row.id }}</div>
          </td>
          <td class="col-field">
            <div class="clip-cell" [pTooltip]="row.fieldOrColumn" tooltipPosition="top">
              {{ row.fieldOrColumn }}
            </div>
            <div class="reasons" *ngIf="row.isSpecialCase">{{ formatReasons(row.specialReasons) }}</div>
          </td>
          <td class="col-value">
            <div class="value-cell">{{ row.value }}</div>
          </td>
        </tr>
      </ng-template>
      <ng-template pTemplate="emptymessage">
        <tr>
          <td colspan="6">No matches.</td>
        </tr>
      </ng-template>
    </p-table>
    </div>
    </ng-container>
  </div>

  <p-dialog
    [(visible)]="rollbackDialogVisible"
    header="Rollback last Apply"
    [modal]="true"
    [dismissableMask]="true"
    [style]="{ width: 'min(96vw, 72rem)' }"
    [contentStyle]="{ 'max-height': '70vh', overflow: 'auto' }"
    [closable]="!rollbackApplying"
    (onHide)="closeRollbackDialog()"
    styleClass="rename-rollback-dialog"
  >
    <div class="rollback-dialog-meta" *ngIf="pendingRollback as snap">
      <p>
        Restores values from
        <strong>"{{ snap.to }}"</strong> → <strong>"{{ snap.from }}"</strong>
        ({{ snap.appliedCount || rollbackChanges.length }} change{{
          (snap.appliedCount || rollbackChanges.length) === 1 ? '' : 's'
        }}).
      </p>
      <p class="rollback-dialog-sub" *ngIf="snap.appliedAt">
        Applied {{ snap.appliedAt | date: 'medium' }}
        <span *ngIf="snap.triggeredBy?.email || snap.triggeredBy?.name">
          · by {{ snap.triggeredBy?.email || snap.triggeredBy?.name }}
        </span>
        · kept up to 1 month · one-shot
      </p>
    </div>

    <p-table
      [value]="rollbackChanges"
      [paginator]="true"
      [rows]="rollbackDialogRows"
      [first]="rollbackDialogFirst"
      [rowsPerPageOptions]="[25, 50, 100]"
      (onPage)="rollbackDialogFirst = $event.first; rollbackDialogRows = $event.rows"
      styleClass="p-datatable-sm p-datatable-striped rename-results-table"
      [scrollable]="true"
      scrollHeight="360px"
      [tableStyle]="{ 'min-width': '56rem' }"
    >
      <ng-template pTemplate="header">
        <tr>
          <th class="col-flags">Flags</th>
          <th class="col-source">Source</th>
          <th class="col-collection">Collection / Table</th>
          <th class="col-id">ID</th>
          <th class="col-field">Field / Column</th>
          <th class="col-value">Current (after apply)</th>
          <th class="col-value">Restore to</th>
        </tr>
      </ng-template>
      <ng-template pTemplate="body" let-row>
        <tr [class.special-row]="row.isSpecialCase">
          <td class="col-flags">
            <span *ngIf="row.isSpecialCase" class="flag special" [pTooltip]="formatReasons(row.specialReasons)">
              Special
            </span>
            <span *ngIf="!row.isSpecialCase" class="flag safe">Safe</span>
          </td>
          <td class="col-source">{{ row.source }}</td>
          <td class="col-collection">
            <div class="clip-cell" [pTooltip]="row.collectionOrTable" tooltipPosition="top">
              {{ row.collectionOrTable }}
            </div>
          </td>
          <td class="col-id">
            <div class="id-cell" [pTooltip]="row.id" tooltipPosition="top">{{ row.id }}</div>
          </td>
          <td class="col-field">
            <div class="clip-cell" [pTooltip]="row.fieldOrColumn" tooltipPosition="top">
              {{ row.fieldOrColumn }}
            </div>
          </td>
          <td class="col-value">
            <div class="value-cell">{{ row.newValue }}</div>
          </td>
          <td class="col-value">
            <div class="value-cell">{{ row.oldValue }}</div>
          </td>
        </tr>
      </ng-template>
      <ng-template pTemplate="emptymessage">
        <tr>
          <td colspan="7">No changes available for rollback.</td>
        </tr>
      </ng-template>
    </p-table>

    <ng-template pTemplate="footer">
      <button
        pButton
        type="button"
        label="Cancel"
        class="p-button-text"
        [disabled]="rollbackApplying"
        (click)="closeRollbackDialog()"
      ></button>
      <button
        pButton
        type="button"
        label="Apply rollback"
        icon="pi pi-replay"
        class="p-button-warning"
        [disabled]="!canRollback || rollbackApplying || rollbackChanges.length === 0"
        [loading]="rollbackApplying"
        (click)="executeRollbackFromDialog()"
      ></button>
    </ng-template>
  </p-dialog>
</div>
